import type { AttendeeRemovedAgendaItem, PublicConventionGuide } from "@verity-connect/contracts";
import { readConventionWindow } from "@verity-connect/domain";

/**
 * One day of the convention as a single spine: a rail of times, one row per thing
 * that happens, and the block that is running now sitting at its own place in the
 * line rather than in a separate card above it.
 *
 * The day is drawn in full whether or not anything has been saved. Most of a
 * convention day is the same for everybody — when the opening session starts, when
 * lunch is, when the closing session runs — so the shape is there from the first
 * time somebody opens the app, and each seminar row becomes a session as it is
 * chosen.
 */
export type RowState = "past" | "now" | "next" | "ahead";
export type SessionWorkspaceMode = "before" | "during" | "after";

/**
 * The session page changes priority with the clock without ever hiding the notes.
 *
 * All three states have a workspace, and "before" is the one that was missing. A teacher
 * choosing between ten seminars in a slot has reasons — a question she wants to ask, a
 * problem in her own classroom she is hoping this answers — and those are worth writing
 * down when she has them rather than when the room fills up. Withholding the notes until
 * the session starts also meant nobody could open the screen at all until convention
 * morning, which is a poor way to find out whether it works.
 */
export function sessionWorkspaceMode(state: RowState): SessionWorkspaceMode {
  return state === "now" ? "during" : state === "past" ? "after" : "before";
}

export interface DayTimelineRow {
  /** Stable across a save, so React keeps the row rather than replacing it. */
  key: string;
  blockId: string;
  blockTitle: string;
  /** The day this row is on, "2026-09-17". A start and an end are not a moment without it. */
  date: string;
  startTime: string;
  endTime: string;
  state: RowState;
  /** The activity title, or the block name while the choice is open. */
  title: string;
  /** "Completed · Chapel", "Saved · SC 101", "Choose from 10 sessions". */
  detail: string;
  href: string;
  /** Set when this row is one activity rather than an open choice. */
  itemId: string | null;
  roomCode: string | null;
  /** The same room as a person reads it. The code above is what a link is built from. */
  roomLabel: string | null;
  presenter: string | null;
  /** True when the attendee picked this themselves. */
  chosen: boolean;
  /** True when the block still holds a choice nobody has made. */
  awaitingChoice: boolean;
  /** How many sessions the block holds, for the open-choice wording. */
  choiceCount: number;
  /**
   * The session she saved here that the association has since removed.
   *
   * Set only when the removal has left her with nothing in this slot. Her saved
   * row is never deleted, so without this the block reverts to "Choose from ten
   * sessions" and reads exactly like a choice she never made.
   */
  removedTitle: string | null;
}

/**
 * The day the event is on right now: the day holding whatever is running, or the
 * day holding whatever is next, or — once nothing is running and nothing is left —
 * the last day. Before the event begins the next block is on day one, so day one
 * is what comes back.
 *
 * Every screen that has to pick a day asks this. Two screens working it out
 * separately is how the schedule came to open on Thursday for a teacher standing
 * in a hallway on Friday.
 */
export function scheduleDayId(
  guide: PublicConventionGuide,
  currentBlockId: string | null,
  nextBlockId: string | null,
): string {
  const dayOf = (blockId: string | null) =>
    blockId ? guide.blocks.find((block) => block.id === blockId)?.dayId : undefined;
  return dayOf(currentBlockId)
    ?? dayOf(nextBlockId)
    ?? guide.days[guide.days.length - 1]?.id
    ?? "";
}

/** One day's blocks in the order they happen. */
export function orderedBlocks(
  guide: PublicConventionGuide,
  dayId: string,
): readonly PublicConventionGuide["blocks"][number][] {
  return guide.blocks
    .filter((block) => block.dayId === dayId)
    .slice()
    .sort((left, right) => left.startTime.localeCompare(right.startTime));
}

/**
 * Where the clock has reached in one day.
 *
 * The clock is not always on the day being looked at. A teacher opening Thursday
 * on the Friday morning is looking at a day that is wholly finished, and one
 * opening Friday on the Thursday is looking at a day where nothing has run yet.
 * So the day is placed against the day the clock is on first, and only a day the
 * clock is actually inside gets examined block by block.
 */
export function blockStates(
  guide: PublicConventionGuide,
  dayId: string,
  currentBlockId: string | null,
  nextBlockId: string | null,
): ReadonlyMap<string, RowState> {
  const blocks = orderedBlocks(guide, dayId);
  const states = new Map<string, RowState>();
  const marker = guide.blocks.find((block) => block.id === (currentBlockId ?? nextBlockId));
  const dayIndex = guide.days.findIndex((day) => day.id === dayId);
  const markerDayIndex = marker ? guide.days.findIndex((day) => day.id === marker.dayId) : -1;

  // Nothing running and nothing left means the event is over: every day is done.
  const allPast = !marker || (markerDayIndex >= 0 && dayIndex >= 0 && dayIndex < markerDayIndex);
  const allAhead = Boolean(marker) && markerDayIndex >= 0 && dayIndex >= 0 && dayIndex > markerDayIndex;
  const markerIndex = blocks.findIndex((block) => block.id === marker?.id);

  for (const [index, block] of blocks.entries()) {
    states.set(
      block.id,
      allPast
        ? "past"
        : allAhead
          ? "ahead"
          : block.id === currentBlockId
            ? "now"
            : block.id === nextBlockId
              ? "next"
              : markerIndex >= 0 && index < markerIndex
                ? "past"
                : "ahead",
    );
  }
  return states;
}

export function dayTimeline(
  guide: PublicConventionGuide,
  savedItemIds: ReadonlySet<string>,
  dayId: string,
  currentBlockId: string | null,
  nextBlockId: string | null,
  /** What she saved that is no longer on the schedule, from her own agenda. */
  removed: readonly AttendeeRemovedAgendaItem[] = [],
): readonly DayTimelineRow[] {
  const blocks = orderedBlocks(guide, dayId);
  const states = blockStates(guide, dayId, currentBlockId, nextBlockId);
  const date = guide.days.find((day) => day.id === dayId)?.date ?? "";
  const rows: DayTimelineRow[] = [];

  for (const block of blocks) {
    const items = block.itemIds
      .map((id) => guide.items.find((item) => item.id === id))
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    const saved = items.filter((item) => savedItemIds.has(item.id));
    const state: RowState = states.get(block.id) ?? "ahead";
    // Only when the removal left the slot empty. A teacher who saved two sessions
    // in one slot and lost one still has somewhere to be, and does not need telling.
    const lost = saved.length === 0
      ? removed.find((entry) => entry.blockId === block.id)
      : undefined;

    const base = {
      blockId: block.id,
      blockTitle: block.title,
      date,
      startTime: block.startTime,
      endTime: block.endTime,
      state,
    };

    /*
     * Her slot is empty because the session she chose was taken off the schedule.
     *
     * This runs ahead of every other shape a row can take, including the one that
     * draws a block holding a single session as a fixed activity for everybody. A
     * block that held two and lost one would otherwise announce the survivor as
     * though she had been going to it all along.
     */
    if (lost) {
      rows.push({
        ...base,
        key: `${block.id}-removed`,
        title: block.title,
        // Said once, in the row where her session used to be. Once the slot has
        // passed there is nothing left to choose, so it stops asking and only
        // says what happened.
        // The dead session is the explanation, never the headline. The row leads
        // with the slot and what to do about it, exactly like any other unmade
        // choice, and names what she lost underneath.
        // Just the name. The label above the row already says what happened to it,
        // and saying "removed" twice in four lines reads as a system talking to
        // itself rather than to her.
        detail: lost.title,
        // The block, so she lands on the slot she is being asked to fill.
        href: `/schedule?day=${encodeURIComponent(dayId)}&block=${encodeURIComponent(block.id)}`,
        itemId: null,
        roomCode: null,
        roomLabel: null,
        presenter: null,
        chosen: false,
        awaitingChoice: state !== "past" && items.length > 0,
        choiceCount: items.length,
        removedTitle: lost.title,
      });
      continue;
    }

    // Both are shown when two sessions are saved in one slot, because that clash is
    // the thing somebody needs to see.
    if (saved.length > 0) {
      for (const item of saved) {
        const roomCode = roomCodeOf(guide, item.roomId);
        const roomLabel = roomLabelOf(guide, item.roomId);
        rows.push({
          ...base,
          key: item.id,
          title: item.title,
          detail: state === "past" ? withRoom("Completed", roomLabel) : withRoom("Saved", roomLabel),
          href: `/schedule/${encodeURIComponent(item.id)}`,
          itemId: item.id,
          roomCode,
          roomLabel,
          presenter: presenterNames(guide, item.presenterIds),
          chosen: true,
          awaitingChoice: false,
          choiceCount: items.length,
          removedTitle: null,
        });
      }
      continue;
    }

    const only = items.length === 1 ? items[0] : undefined;
    if (only) {
      const roomCode = roomCodeOf(guide, only.roomId);
      const roomLabel = roomLabelOf(guide, only.roomId);
      rows.push({
        ...base,
        key: block.id,
        title: only.title,
        // Fixed for everybody, so there is no "Saved" to claim: before it runs the
        // row names the room or the presenter, and afterwards it says it is done.
        detail: state === "past"
          ? withRoom("Completed", roomLabel)
          : roomLabel ?? presenterNames(guide, only.presenterIds) ?? "Everyone",
        href: `/schedule/${encodeURIComponent(only.id)}`,
        itemId: only.id,
        roomCode,
        roomLabel,
        presenter: presenterNames(guide, only.presenterIds),
        chosen: false,
        awaitingChoice: false,
        choiceCount: 1,
        removedTitle: null,
      });
      continue;
    }

    rows.push({
      ...base,
      key: block.id,
      title: block.title,
      detail: items.length === 0
        ? "Details to come"
        : state === "past"
          ? "No session saved"
          : `Choose from ${items.length} sessions`,
      /*
       * The block, not just its day.
       *
       * "Seminar 1 · 9 sessions · Choose your session" used to land on the whole day's
       * schedule at the top, leaving somebody to find Seminar 1 and tap it a second time.
       * Every block holding a choice behaved that way, which is most of a convention
       * morning.
       */
      href: `/schedule?day=${encodeURIComponent(dayId)}&block=${encodeURIComponent(block.id)}`,
      itemId: null,
      roomCode: null,
      roomLabel: null,
      presenter: null,
      chosen: false,
      awaitingChoice: items.length > 0,
      choiceCount: items.length,
      removedTitle: null,
    });
  }
  return rows;
}

/** "Completed · Chapel", "Saved · SC 101", or just the word when no room is known. */
function withRoom(word: string, room: string | null): string {
  return room ? `${word} · ${room}` : word;
}

/**
 * How far through the running block the clock is, from 0 to 1, for the bar under
 * the card. Null when nothing is running, so the bar is simply absent.
 *
 * Read in the event's own zone, like every other time decision here. It used to
 * read the hours and minutes off the device, which is the same number only for
 * somebody whose phone happens to agree with the convention — a teacher arriving
 * from a Central-time school saw the bar an hour out of place all day.
 */
export function blockProgress(
  row: DayTimelineRow | undefined,
  now: Date,
  timeZone: string,
): number | null {
  if (!row || row.state !== "now") return null;
  try {
    return readConventionWindow(
      { date: row.date, startTime: row.startTime, endTime: row.endTime },
      now,
      timeZone,
    ).fraction;
  } catch {
    // A zone or a time the browser cannot read. No bar at all is better than a wrong one.
    return null;
  }
}

/**
 * The part of the day worth showing on the home screen: what is running now, or
 * next, with what led up to it and what follows. Never cuts off the running block.
 */
export function homeRows(rows: readonly DayTimelineRow[], limit: number): readonly DayTimelineRow[] {
  if (rows.length <= limit) return rows;
  const focus = rows.findIndex((row) => row.state === "now" || row.state === "next");
  if (focus < 0) return rows.slice(-limit);
  // One row of context above the focus, the rest below it.
  const start = Math.min(Math.max(0, focus - 1), rows.length - limit);
  return rows.slice(start, start + limit);
}

function roomCodeOf(guide: PublicConventionGuide, roomId: string | null): string | null {
  if (!roomId) return null;
  return guide.rooms.find((candidate) => candidate.id === roomId)?.code ?? null;
}

/**
 * What the row calls the room, which is not always its code: the chapel's code is its
 * name in capitals, and shouting it is not naming it. The code stays as it is, because
 * the map and the filters are keyed on it.
 */
function roomLabelOf(guide: PublicConventionGuide, roomId: string | null): string | null {
  if (!roomId) return null;
  const room = guide.rooms.find((candidate) => candidate.id === roomId);
  if (!room) return null;
  return room.code.toLocaleLowerCase("en-US") === room.name.toLocaleLowerCase("en-US") ? room.name : room.code;
}

function presenterNames(guide: PublicConventionGuide, presenterIds: readonly string[]): string | null {
  const names = presenterIds
    .map((id) => guide.presenters.find((candidate) => candidate.id === id)?.displayName)
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * Saving a session, from a row or from the session's own page.
 *
 * One seminar to a block: choosing a second replaces the first rather than putting a
 * teacher in two rooms at once. It lives here, away from either screen, because both
 * reach it and two copies of a rule this small would drift without anybody noticing.
 */
export function toggleSavedItem(guide: PublicConventionGuide, savedItemIds: readonly string[], item: { id: string; blockId: string }): readonly string[] {
  if (savedItemIds.includes(item.id)) return savedItemIds.filter((id) => id !== item.id);
  const withoutConflict = savedItemIds.filter((id) => guide.items.find((candidate) => candidate.id === id)?.blockId !== item.blockId);
  return [...withoutConflict, item.id];
}
